import { useCallback, useEffect, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { InAppReview } from "@capacitor-community/in-app-review";
import { DeviceType, ReviewRequest, useAuth, useSchool, useSettings } from "daymap-react";
import { Device } from "@capacitor/device";
import dayjs from "dayjs";
import { Capacitor } from "@capacitor/core";

const STORAGE_KEYS = {
  routes: "app_review_routes",
  days: "app_review_days",
  lastPrompt: "app_review_last_prompt",
};

interface UseInAppReviewOptions {
  requiredRoutes?: string[]; // e.g. ["/home", "/profile/:id", "/settings/*"]
  minRoutesVisited?: number; // default 2
  minDaysUsed?: number; // default 5
  cooldownDays?: number; // default 7
}

const defaultRoutes = [
    '/message/:messageId',
    '/student/:studentId/timetable/:eventId',
    '/student/:studentId/attendance',
    '/student/:studentId/excursion/:eventId',
    '/student/:studentId/schedule',
    '/student/:studentId/reports',
    '/student/:studentId/attachments',
    '/student/:studentId/assessment/:taskid',
    '/interviews/:groupId',
    '/mydetails',
];

export const useInAppReview = ({
  requiredRoutes = defaultRoutes,
  minRoutesVisited = 3,
  minDaysUsed = 5,
  cooldownDays = 7,
}: UseInAppReviewOptions = {}) => {
  const location = useLocation();
  const [visitedRoutes, setVisitedRoutes] = useState<string[]>(JSON.parse(
    localStorage.getItem(STORAGE_KEYS.routes) || "[]"
  ));
  const [usedDays, setUsedDays] = useState<string[]>(JSON.parse(
    localStorage.getItem(STORAGE_KEYS.days) || "[]"
  ));
  const { Post } = useAuth();
  const { baseUrl } = useSchool();
  const { isAtLeastThisVersion } = useSettings();

  // --- Track visited routes (with pattern matching) ---
  useEffect(() => {
    if (!isAtLeastThisVersion(11, 7) || !Capacitor.isNativePlatform()) return; 

    const visited = new Set<string>(
      JSON.parse(localStorage.getItem(STORAGE_KEYS.routes) || "[]")
    );

    const matchedRoute = requiredRoutes.find((pattern) =>
      matchPath({ path: pattern, end: false }, location.pathname)
    );

    if (matchedRoute) {
      visited.add(matchedRoute); // store the *pattern*, not the exact URL
      localStorage.setItem(STORAGE_KEYS.routes, JSON.stringify([...visited]));
      setVisitedRoutes([...visited]);
    }
  }, [location.pathname]);

  // --- Track usage days ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return; 

    const today = dayjs().format('YYYY-MM-DD');
    const days = new Set<string>(
      JSON.parse(localStorage.getItem(STORAGE_KEYS.days) || "[]")
    );
    days.add(today);
    localStorage.setItem(STORAGE_KEYS.days, JSON.stringify([...days]));
    setUsedDays([...days]);
  }, []);

  // --- Check conditions and maybe request review ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return; 
    
    async function maybeRequestReview() {
      const lastPrompt = localStorage.getItem(STORAGE_KEYS.lastPrompt);
      const enoughRoutes = visitedRoutes.length >= minRoutesVisited;
      const enoughDays = usedDays.length >= minDaysUsed;

      let outsideCooldown = true;
      if (lastPrompt) {
        const last = new Date(lastPrompt);
        const diffDays = Math.floor(
          (Date.now() - last.getTime()) / (1000 * 60 * 60 * 24)
        );
        outsideCooldown = diffDays >= cooldownDays;
      }

      if (enoughRoutes && enoughDays && outsideCooldown) {
        console.log("✅ Requesting app review");
        let error = "";
        try {
          await InAppReview.requestReview();
        } catch (err) {
          console.warn("In-app review failed", err);
          error = ''+err;
        }

        sendReviewRequestDetails(error);
        
        localStorage.setItem(STORAGE_KEYS.lastPrompt, new Date().toISOString());
        localStorage.removeItem(STORAGE_KEYS.days);
        localStorage.removeItem(STORAGE_KEYS.routes);
        setUsedDays([]);
        setVisitedRoutes([]);
      }
    }

    maybeRequestReview();
  }, [visitedRoutes, usedDays]);

  const sendReviewRequestDetails = useCallback(async (error:string = "") => {
    const prevReview = localStorage.getItem(STORAGE_KEYS.lastPrompt);
    const device = await Device.getInfo();
    const data: ReviewRequest = {
      Details: JSON.stringify({ visitedRoutes, usedDays }),
      PreviousRequestDate: prevReview ? new Date(prevReview) : null,
      Error: error,
      DeviceType: device.operatingSystem === "android" ? DeviceType.Android : device.operatingSystem === "ios" ? DeviceType.Apple : DeviceType.NotSet
    }
    Post({ url: `${baseUrl}/lms/api/contact/logreviewrequest`, data, config: { method: 'POST' } })
  }, [visitedRoutes, usedDays])
};